import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';

import {
  ChangeGroupSizeChangeType,
  ChangeGroupSizePanelOptions,
  PanelHostService,
} from '@services/panel-host.service';
import { TripService } from '@services/trip.service';
import { TripFinanceService } from '@services/trip-finance.service';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

const SPACE_OPTIONS: readonly string[] = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
];

@Component({
  selector: 'app-change-group-size',
  imports: [FormsModule, TranslocoDirective, ContentContainerComponent, PanelBodyComponent],
  templateUrl: './change-group-size.component.html',
  styleUrls: ['./change-group-size.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeGroupSizeComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly spaceOptions = SPACE_OPTIONS;

  constructor() {
    effect(() => {
      const panel = this.panelHost.activePanel();
      const options = this.panelHost.changeGroupSizeOptions();
      if (panel === 'change-group-size' && options) {
        this.initializeFromOptions(options);
      }
    });
  }

  changeType = signal<ChangeGroupSizeChangeType>('add');
  /** Target roster size after confirm */
  targetCount = signal<number>(1);
  /** Spaces to add when changeType is add */
  spacesToAdd = signal<string>('1');
  /** Bulk add count when changeType is addCount */
  bulkAddCount = signal<number>(1);
  submitting = signal<boolean>(false);

  readonly showSpaceSelect = computed(() => this.changeType() === 'add');

  readonly addCountDescriptionParams = computed(() => ({
    count: this.targetCount(),
    addCount: this.bulkAddCount(),
  }));

  readonly countDescriptionParams = computed(() => ({
    count: this.targetCount(),
  }));

  private initializeFromOptions(options: ChangeGroupSizePanelOptions): void {
    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    const currentAttendees = trip.attendees ?? 0;
    let changeType = options.changeType;
    const bulkAddCount = options.count ?? 1;

    if (
      changeType === 'add' ||
      (changeType === 'addCount' && options.count === 1)
    ) {
      changeType = 'add';
      this.spacesToAdd.set('1');
      this.syncTargetCountFromSpacesToAdd();
    } else if (changeType === 'remove') {
      const nextCount = currentAttendees - 1;
      this.targetCount.set(nextCount <= 0 ? 1 : nextCount);
    } else if (changeType === 'addCount') {
      this.targetCount.set(currentAttendees + bulkAddCount);
    }

    this.changeType.set(changeType);
    this.bulkAddCount.set(bulkAddCount);
    this.updatePanelHeaderTitle(changeType);
  }

  onSpacesToAddChange(): void {
    this.syncTargetCountFromSpacesToAdd();
  }

  private syncTargetCountFromSpacesToAdd(): void {
    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }
    this.targetCount.set((trip.attendees ?? 0) + Number(this.spacesToAdd()));
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  confirm(): void {
    if (this.submitting()) {
      return;
    }

    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    this.submitting.set(true);
    const count = this.targetCount();

    this.tripService
      .updateParticipantCount(trip.id, count)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          this.tripFinanceService.notifyFinanceChanged();

          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.change-group-size.success'),
              { count },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.change-group-size.error'),
            ),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  private updatePanelHeaderTitle(changeType: ChangeGroupSizeChangeType): void {
    const titleKey =
      changeType === 'add'
        ? marker('group-management.change-group-size.add-heading')
        : changeType === 'remove'
          ? marker('group-management.change-group-size.remove-heading')
          : marker('group-management.change-group-size.add-count-heading');

    this.panelHost.changeGroupSizeHeaderTitle.set(
      this.transloco.translate(titleKey),
    );
  }
}
